// TopBar.tsx — Menu bar mirroring v11 csv.mnu structure
// Menu items map to progcont values / activeDialog names in useStore
// Menus: File | Panel | Site | View | Utilities | Export | Help

import { useRef, useState, useEffect } from 'react';
import { useStore, useShallowStore } from '../store/useStore';
import type { ActiveDialog } from '../types/panelvar';
import SnapshotBrowser from './SnapshotBrowser';
import { useAuth } from '../auth/AuthProvider';
import { membershipBadge, membershipDetail, formatPlan } from '../auth/billing';

interface MenuItem {
  label: string;
  dialog?: ActiveDialog;
  action?: () => void;
  divider?: boolean;
}

interface Menu {
  label: string;
  items: MenuItem[];
}

export default function TopBar() {
  const { project, setActiveDialog, closeProject, saveProject, editMode, setEditMode, activeDialog, viewMode, setViewMode, elevationViewMode, setElevationViewMode, buildingViewMode, setBuildingViewMode } = useShallowStore(s => ({
    project:              s.project,
    setActiveDialog:      s.setActiveDialog,
    closeProject:         s.closeProject,
    saveProject:          s.saveProject,
    editMode:             s.ui.editMode,
    setEditMode:          s.setEditMode,
    activeDialog:         s.ui.activeDialog,
    viewMode:             s.ui.viewMode,
    setViewMode:          s.setViewMode,
    elevationViewMode:    s.ui.elevationViewMode,
    setElevationViewMode: s.setElevationViewMode,
    buildingViewMode:     s.ui.buildingViewMode,
    setBuildingViewMode:  s.setBuildingViewMode,
  }));

  const { user, signOut, membership, balance } = useAuth();
  const badge = membershipBadge(membership);
  const detail = membershipDetail(membership);
  const planLabel = formatPlan(membership?.plan ?? null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const userLabel = (user?.user_metadata?.full_name as string | undefined)
    ?? (user?.user_metadata?.name as string | undefined)
    ?? user?.email
    ?? '';

  async function handleSignOut() {
    setUserMenuOpen(false);
    await signOut();
  }

  // Allow other components to open snapshot browser via activeDialog
  useEffect(() => {
    if (activeDialog === 'snapshots') {
      setShowSnapshots(true);
      setActiveDialog('none');
    }
  }, [activeDialog, setActiveDialog]);

  const menus: Menu[] = [
    {
      label: 'File',
      items: [
        { label: 'New Project',     action: () => useStore.getState().newProject() },
        { label: 'Open Project…',   dialog: 'project-load' },
        { label: 'Save Project',    action: () => saveProject() },
        { label: '', divider: true },
        { label: 'Import DXF…',       dialog: 'import-dxf' },
        { label: 'Import CV File…',   dialog: 'import-cv' },
        { label: '', divider: true },
        { label: 'Export Project (.cvp)',  dialog: 'export-cvp' },
        { label: 'Export Template (.cvt)', dialog: 'export-cvt' },
        { label: '', divider: true },
        { label: 'Export Panel DXF',  dialog: 'export-panel-dxf' },
        { label: 'Export Site DXF',   dialog: 'export-site-dxf' },
        { label: 'Export Panel Book', dialog: 'panel-book-pdf' },
        { label: '', divider: true },
        { label: 'Snapshots…',       action: () => setShowSnapshots(true) },
      ],
    },
    {
      label: 'Edit',
      items: [
        { label: 'Project',    action: () => setEditMode('project') },
        { label: 'Building',   action: () => setEditMode('building') },
        { label: 'Elevations', action: () => setEditMode('elevations') },
        { label: 'Brace Plan', action: () => setEditMode('brace') },
        { label: 'Cast Layout', action: () => setEditMode('cast') },
        { label: 'Panel',      action: () => setEditMode('panel') },
      ],
    },

    // Elevations menu — only visible when in elevations edit mode
    ...(editMode === 'elevations' ? [{
      label: 'Elevations',
      items: [
        { label: 'Site Setup',         dialog: 'site-setup' as ActiveDialog },
        { label: 'Place Panel',        dialog: 'site-place' as ActiveDialog },
        { label: 'Edit Placement',     dialog: 'site-edit' as ActiveDialog },
        { label: 'Site Dimensions',    dialog: 'site-dims' as ActiveDialog },
      ],
    }] : []),
    {
      label: 'Utilities',
      items: [
        { label: 'Drawing Setup',      dialog: 'drawing-setup' },
        { label: 'Revision Notes',     dialog: 'revision-notes' },
        { label: 'Slope Calculator',   dialog: 'slope-calc' },
        { label: 'Materials List',     dialog: 'materials-list' },
        { label: 'Batch Utilities',    dialog: 'batch-utilities' },
      ],
    },
    {
      label: 'Export',
      items: [
        { label: 'Panel DXF',        dialog: 'export-panel-dxf' },
        { label: 'Site DXF',          dialog: 'export-site-dxf' },
        { label: 'Panel Book PDF',    dialog: 'panel-book-pdf' },
        { label: 'Materials CSV',     dialog: 'materials-list' },
        { label: '', divider: true },
        { label: 'Project (.cvp)',    dialog: 'export-cvp' },
        { label: 'Template (.cvt)',   dialog: 'export-cvt' },
        { label: 'Panel (.cvpanel)',  dialog: 'export-cvpanel' },
        { label: 'Site (.cvsite)',    dialog: 'export-cvsite' },
      ],
    },
    {
      label: 'Help',
      items: [
        { label: 'About ConstructiVision', dialog: 'about' },
      ],
    },
  ];

  function handleItem(item: MenuItem) {
    setOpenMenu(null);
    if (item.dialog) setActiveDialog(item.dialog);
    else if (item.action) item.action();
  }

  return (
    <>
    <header className="app-topbar" ref={barRef} onMouseLeave={() => setOpenMenu(null)}>
      <a
        href="../index.html"
        className="topbar-back"
        title="Back to SimpleStruct"
      >
        &#8592; SimpleStruct
      </a>
      <button
        className="topbar-logo"
        title="Close project — return to home"
        onClick={closeProject}
      >
        CV
      </button>

      <nav className="topbar-menus">
        {menus.map((menu, i) => (
          <div
            key={menu.label}
            className="topbar-menu-root"
            onMouseEnter={() => openMenu !== null && setOpenMenu(i)}
          >
            <button
              className={`topbar-btn${openMenu === i ? ' active' : ''}`}
              onClick={() => setOpenMenu(openMenu === i ? null : i)}
            >
              {menu.label}
            </button>
            {openMenu === i && (
              <ul className="topbar-dropdown">
                {menu.items.map((item, j) =>
                  item.divider ? (
                    <li key={`d${j}`} className="topbar-divider" />
                  ) : (
                    <li key={item.label}>
                      <button
                        className="topbar-dropdown-item"
                        onClick={() => handleItem(item)}
                      >
                        {item.label}
                      </button>
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        ))}
      </nav>

      <div className="topbar-right">
        {project && (
          <>
            <div className="topbar-mode-group" role="group" aria-label="Edit mode">
              <button className={`topbar-mode-btn${editMode === 'project' ? ' active' : ''}`} onClick={() => setEditMode('project')}>Project</button>
              <button className={`topbar-mode-btn${editMode === 'building' ? ' active' : ''}`} onClick={() => setEditMode('building')}>Building</button>
              <button className={`topbar-mode-btn${editMode === 'elevations' ? ' active' : ''}`} onClick={() => setEditMode('elevations')}>Elevations</button>
              <button className={`topbar-mode-btn${editMode === 'brace' ? ' active' : ''}`} onClick={() => setEditMode('brace')}>Brace</button>
              <button className={`topbar-mode-btn${editMode === 'cast' ? ' active' : ''}`} onClick={() => setEditMode('cast')}>Cast</button>
              <button className={`topbar-mode-btn${editMode === 'panel' ? ' active' : ''}`} onClick={() => setEditMode('panel')}>Panel</button>
            </div>
            {editMode === 'panel' && (
              <div className="topbar-view-group" role="group" aria-label="View mode">
                <button className={`topbar-view-btn${viewMode !== '2d' ? ' active' : ''}`} onClick={() => setViewMode('viewMode', '3d')}>3D</button>
                <button className={`topbar-view-btn${viewMode === '2d' ? ' active' : ''}`} onClick={() => setViewMode('viewMode', '2d')}>2D</button>
              </div>
            )}
            {editMode === 'elevations' && (
              <div className="topbar-view-group" role="group" aria-label="View mode">
                <button className={`topbar-view-btn${elevationViewMode === '3d' ? ' active' : ''}`} onClick={() => setElevationViewMode('3d')}>3D</button>
                <button className={`topbar-view-btn${elevationViewMode === '2d' ? ' active' : ''}`} onClick={() => setElevationViewMode('2d')}>2D</button>
              </div>
            )}
            {editMode === 'building' && (
              <div className="topbar-view-group" role="group" aria-label="View mode">
                <button className={`topbar-view-btn${buildingViewMode === '3d' ? ' active' : ''}`} onClick={() => setBuildingViewMode('3d')}>3D</button>
                <button className={`topbar-view-btn${buildingViewMode === '2d' ? ' active' : ''}`} onClick={() => setBuildingViewMode('2d')}>2D</button>
              </div>
            )}
            <span className="topbar-project-name">{project.name}</span>
            <button
              className="topbar-btn topbar-project-settings-btn"
              onClick={() => setActiveDialog('project-settings')}
              title="Project Properties"
            >
              ⚙️
            </button>
          </>
        )}
        {user && (
          <div className="topbar-user-root">
            <button
              className={`topbar-user-btn${userMenuOpen ? ' active' : ''}`}
              onClick={() => setUserMenuOpen(o => !o)}
              title={user.email ?? ''}
            >
              <span className={`topbar-user-badge topbar-user-badge-${badge.kind}`}>
                {badge.label}
              </span>
              <span className="topbar-user-credits" title="Panel credits">{balance}¢</span>
              <span className="topbar-user-name">{userLabel}</span>
              <span className="topbar-user-caret" aria-hidden>▾</span>
            </button>
            {userMenuOpen && (
              <ul className="topbar-dropdown topbar-user-dropdown" onMouseLeave={() => setUserMenuOpen(false)}>
                <li className="topbar-user-meta">{user.email}</li>
                <li className="topbar-user-membership">
                  <div className="topbar-user-membership-row">
                    <span className={`billing-badge billing-badge-${badge.kind}`}>{badge.label}</span>
                    {planLabel && <span className="topbar-user-membership-plan">{planLabel}</span>}
                  </div>
                  {detail && <div className="topbar-user-membership-detail">{detail}</div>}
                  <div className="topbar-user-membership-detail">
                    <strong>{balance}</strong> panel credit{balance === 1 ? '' : 's'}
                  </div>
                </li>
                <li className="topbar-divider" />
                <li>
                  <a
                    className="topbar-dropdown-item"
                    href={`${import.meta.env.BASE_URL}billing`}
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Billing history
                  </a>
                </li>
                <li>
                  <a
                    className="topbar-dropdown-item"
                    href={`${import.meta.env.BASE_URL}pricing`}
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Buy credits or upgrade
                  </a>
                </li>
                <li className="topbar-divider" />
                <li>
                  <button className="topbar-dropdown-item" onClick={handleSignOut}>
                    Sign out
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
    </header>
    <SnapshotBrowser open={showSnapshots} onClose={() => setShowSnapshots(false)} />
    </>
  );
}
